/**
 * The app's QueryClient, with a default error path for mutations (design
 * evaluation F4).
 *
 * A mutation that fails and has no onError of its own toasts the server's message,
 * so no action fails silently. The hook lives on the MutationCache rather than in
 * defaultOptions.mutations.onError because only the cache callback is handed the
 * mutation, and so can see its own options:
 *
 *   - useMutation(fn, { onError })      -> the mutation handles it; no default toast.
 *   - useMutation(fn, { meta: { silentError: true } })
 *                                       -> opt out, e.g. a mutateAsync caller that
 *                                          catches and shows the error inline.
 *   - neither                           -> toast.error(<server message>).
 *
 * Callbacks passed to mutate(vars, { onError }) are per call and not visible here;
 * a site that relies on those should also set meta.silentError.
 */
import { MutationCache, QueryClient } from 'react-query';
import type { Mutation } from 'react-query';
import { toast } from '@/lib/toast';
import { apiErrorMessage } from '@/lib/apiError';

export type MutationErrorMeta = { silentError?: boolean };

export function shouldToastMutationError(mutation: Pick<Mutation<unknown, unknown, unknown, unknown>, 'options'>): boolean {
  const meta = mutation.options.meta as MutationErrorMeta | undefined;
  return !mutation.options.onError && !meta?.silentError;
}

export function createQueryClient(notify: (message: string) => void = (m) => toast.error(m)): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (shouldToastMutationError(mutation)) notify(apiErrorMessage(error, 'The action failed. Try again.'));
      },
    }),
  });
}
